import jsPDF from "jspdf";

export const generateExercisePlanPDF = async (data, t) => {
  const doc = new jsPDF();

  const primaryColor = [5, 117, 230]; // #0575e6
  const lighterColor = [21, 140, 255];
  const HEADER_HEIGHT = 40;

  // --- Utility: Load image with Promise ---
  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
    });
  };

  let backgroundImg = null;
  try {
    backgroundImg = await loadImage("/defaultExercise.png");
  } catch (error) {
    console.warn("Background image failed to load, using blank background.");
  }

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // --- Background + Footer ---
  const addBackgroundAndFooter = (pageNumber = 1) => {
    if (backgroundImg) {
      doc.addImage(backgroundImg, "PNG", 0, 0, pageWidth, pageHeight);
    } else {
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, pageWidth, pageHeight, "F"); // fallback white background
    }

    const pageCount = doc.internal.getNumberOfPages();
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.rect(0, 285, 210, 12, "F");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(
      `Generated on ${new Date().toLocaleDateString()} - Page ${pageNumber} of ${pageCount}`,
      105,
      292,
      { align: "center" }
    );
  };

  // -----------------------------------------------------
  // Page 1 setup
  // -----------------------------------------------------
  addBackgroundAndFooter(1);

  // --- HEADER with gradient ---
  for (let i = 0; i < HEADER_HEIGHT; i += 2) {
    const ratio = i / HEADER_HEIGHT;
    const r = Math.round(
      primaryColor[0] + (lighterColor[0] - primaryColor[0]) * ratio
    );
    const g = Math.round(
      primaryColor[1] + (lighterColor[1] - primaryColor[1]) * ratio
    );
    const b = Math.round(
      primaryColor[2] + (lighterColor[2] - primaryColor[2]) * ratio
    );
    doc.setFillColor(r, g, b);
    doc.rect(0, i, 210, 2, "F");
  }

  // --- Title ---
  doc.setFontSize(22);
  doc.setFont(undefined, "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(data?.title || "Exercise Plan", 55, 25);

  let yPosition = 60;

  // --------------------------
  // Workout Summary
  // --------------------------
  doc.setFontSize(18);
  doc.setFont(undefined, "bold");
  doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.text(t("exercisePlan.workoutSummary"), 20, yPosition);
  yPosition += 15;

  const summaryData = [
    {
      label: t("exercisePlan.duration"),
      value: data?.duration,
      color: [76, 168, 66],
    },
    {
      label: t("exercisePlan.caloriesBurnt"),
      value: data?.calories,
      color: [229, 139, 0],
    },
    {
      label: t("exercisePlan.intensity"),
      value: data?.intensity,
      color: [229, 198, 0],
    },
    {
      label: t("exercisePlan.workouts"),
      value: data?.workouts,
      color: [0, 183, 254],
    },
    {
      label: t("exercisePlan.focus"),
      value: data?.focus,
      color: [229, 0, 103],
    },
  ];

  doc.setFontSize(10);
  summaryData.forEach((item, index) => {
    if (item.value) {
      const xPos = 20 + (index % 2) * 90;
      const yPos = yPosition + Math.floor(index / 2) * 15;

      // Colored circle
      doc.setFillColor(item.color[0], item.color[1], item.color[2]);
      doc.circle(xPos, yPos - 2, 2, "F");

      // Label + value
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, "bold");
      doc.text(`${item.label}:`, xPos + 8, yPos);

      const labelWidth = doc.getTextWidth(`${item.label}: `);
      const spacing = 1;
      const valueXPos = xPos + 8 + labelWidth + spacing;

      doc.setFont(undefined, "normal");
      doc.text(`${item.value}`, valueXPos, yPos);
    }
  });
  yPosition += Math.ceil(summaryData.length / 2) * 15 + 15;

  // --------------------------
  // Description
  // --------------------------
  if (data?.description) {
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text(t("exercisePlan.workoutDescription"), 20, yPosition);
    yPosition += 12; // Define safe area boundary

    const SAFE_Y_BOUNDARY = 275;
    const LINE_HEIGHT = 5; // Height of each line of wrapped text

    doc.setFontSize(11);
    doc.setFont(undefined, "normal");
    doc.setTextColor(50, 50, 50); // Check if description is an object with multiple sections

    if (
      typeof data.description === "object" &&
      !Array.isArray(data.description)
    ) {
      Object.entries(data?.description).forEach(([section, content]) => {
        if (yPosition > SAFE_Y_BOUNDARY - 15) {
          doc.addPage();
          addBackgroundAndFooter(doc.internal.getNumberOfPages());
          yPosition = 20; 
        } 

        doc.setFont(undefined, "bold");
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text(section + ":", 20, yPosition);
        yPosition += 6; 

        doc.setFont(undefined, "normal");
        doc.setTextColor(50, 50, 50);
        const splitText = doc.splitTextToSize(content, 170);
        splitText.forEach((line) => {
          if (yPosition > SAFE_Y_BOUNDARY - LINE_HEIGHT) {
            doc.addPage();
            addBackgroundAndFooter(doc.internal.getNumberOfPages());
            yPosition = 20; 
          }
          doc.text(line, 20, yPosition);
          yPosition += LINE_HEIGHT;
        });

        yPosition += 5; 
      });
    } else {
      const splitText = doc.splitTextToSize(data.description, 170);
      splitText.forEach((line) => {
        if (yPosition > SAFE_Y_BOUNDARY - LINE_HEIGHT) {
          doc.addPage();
          addBackgroundAndFooter(doc.internal.getNumberOfPages());
          yPosition = 20; 
        }
        doc.text(line, 20, yPosition);
        yPosition += LINE_HEIGHT;
      });

      yPosition += 15;
    }
  }
  // --------------------------
  // Workout Breakdown
  // --------------------------
  if (data?.instances && data.instances.length > 0) {
    // console.log(data?.instances);
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text(t("exercisePlan.workoutBreakdown"), 20, yPosition);
    yPosition += 15;

    for (let i = 0; i < data.instances.length; i++) {
      const exercise = data.instances[i];
      const isLeftColumn = i % 2 === 0;
      const xOffset = isLeftColumn ? 20 : 110;

      if (yPosition > 220) {
        doc.addPage();
        addBackgroundAndFooter(doc.internal.getNumberOfPages());
        yPosition = 20;
      }

      // Card background
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(220, 220, 220);
      doc.roundedRect(xOffset - 2, yPosition - 5, 85, 70, 4, 4, "FD");

      // Exercise name
      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      doc.setTextColor(20, 20, 20);
      const exerciseName = exercise.exercise?.name || "Unknown Exercise";
      const nameLines = doc.splitTextToSize(exerciseName, 75);
      doc.text(nameLines, xOffset + 4, yPosition + 8);

      let detailY = yPosition + 8 + nameLines.length * 5 + 2;

      // Exercise details
      const details = [
        {
          label: t("exercisePlan.duration"),
          value: exercise.durationSeconds
            ? `${exercise.durationSeconds} ${t("exercisePlan.seconds")}`
            : null,
          color: [76, 168, 66],
        },
        {
          label: t("exercisePlan.Pace"),
          value: exercise.pace,
          color: [229, 139, 0],
        },
        {
          label: t("exercisePlan.Reps"),
          value: exercise.reps,
          color: [229, 198, 0],
        },
        {
          label: t("exercisePlan.Equipment"),
          value: exercise.exercise?.equipment?.join(", "),
          color: [0, 183, 254],
        },
        {
          label: t("exercisePlan.Muscle"),
          value: exercise.exercise?.muscleGroups?.join(", "),
          color: [229, 0, 103],
        },
      ];

      doc.setFontSize(8);
      doc.setFont(undefined, "normal");
      details.forEach((detail) => {
        if (detail.value && detailY < yPosition + 60) {
          doc.setFillColor(detail.color[0], detail.color[1], detail.color[2]);
          doc.circle(xOffset + 3, detailY - 1, 1.5, "F");
          doc.setTextColor(60, 60, 60);
          doc.text(`${detail.label}: ${detail.value}`, xOffset + 8, detailY);
          detailY += 6;
        }
      });

      if (i % 2 === 1 || i === data.instances.length - 1) {
        yPosition += 75;
      }
    }
  }

  // --------------------------
  // Final Footer Fix
  // --------------------------
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(
      `Generated on ${new Date().toLocaleDateString()} - Page ${i} of ${totalPages}`,
      105,
      292,
      { align: "center" }
    );
  }

  // --------------------------
  // Save PDF
  // --------------------------
  const fileName = data?.title
    ? `${data.title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.pdf`
    : "exercise-plan.pdf";

  doc.save(fileName);
};


